import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  Users,
  User,
  Shield,
  Crown,
  Star,
  Lock,
  Settings,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { getSecurityLevelColor } from '../themes/DarkOpsTheme';
import { fetchPersonas, fetchHealth } from '../lib/api';
import { safeFallback } from '../lib/production-guard';

interface PersonaTier {
  id: string;
  name: string;
  tier: 'Alpha' | 'Beta' | 'Gamma' | 'Delta' | 'Omega';
  status: 'active' | 'suspended' | 'override' | 'locked';
  description: string;
  capabilities: string[];
  accessLevel: number;
  lastAccessed: string;
  overrideCount: number;
  behaviorProfile: {
    initiative: number;
    risktolerance: number;
    autonomy: number;
    collaboration: number;
  };
  securityClearance: string;
  deploymentTarget: string;
}

const mockPersonas: PersonaTier[] = [
  {
    id: 'persona-001',
    name: 'Executive Agent',
    tier: 'Alpha',
    status: 'active',
    description: 'Primary executive-level persona with full system access and autonomous decision-making capabilities',
    capabilities: ['Strategic Planning', 'Resource Allocation', 'System Override', 'Crisis Management', 'Multi-Agent Coordination'],
    accessLevel: 95,
    lastAccessed: '2025-01-26T10:45:00Z',
    overrideCount: 12,
    behaviorProfile: {
      initiative: 95,
      risktolerance: 75,
      autonomy: 90,
      collaboration: 85
    },
    securityClearance: 'CLASSIFIED',
    deploymentTarget: 'Global Command Center'
  },
  {
    id: 'persona-002',
    name: 'Operations Agent',
    tier: 'Beta',
    status: 'active',
    description: 'Operations-focused persona specialized in tactical execution and real-time decision making',
    capabilities: ['Tactical Operations', 'Real-time Analysis', 'Resource Management', 'Team Coordination'],
    accessLevel: 80,
    lastAccessed: '2025-01-26T09:30:00Z',
    overrideCount: 5,
    behaviorProfile: {
      initiative: 85,
      risktolerance: 60,
      autonomy: 75,
      collaboration: 90
    },
    securityClearance: 'SECRET',
    deploymentTarget: 'Operations Center'
  },
  {
    id: 'persona-003',
    name: 'Analytics Agent',
    tier: 'Gamma',
    status: 'override',
    description: 'Data-driven persona optimized for analytical tasks and pattern recognition',
    capabilities: ['Data Analysis', 'Pattern Recognition', 'Predictive Modeling', 'Report Generation'],
    accessLevel: 65,
    lastAccessed: '2025-01-26T08:15:00Z',
    overrideCount: 2,
    behaviorProfile: {
      initiative: 65,
      risktolerance: 40,
      autonomy: 55,
      collaboration: 70
    },
    securityClearance: 'CONFIDENTIAL',
    deploymentTarget: 'Analytics Hub'
  },
  {
    id: 'persona-004',
    name: 'Support Agent',
    tier: 'Delta',
    status: 'suspended',
    description: 'Support-oriented persona designed for assistance and customer interaction tasks',
    capabilities: ['Customer Support', 'Documentation', 'Basic Analysis', 'Query Processing'],
    accessLevel: 45,
    lastAccessed: '2025-01-25T16:22:00Z',
    overrideCount: 0,
    behaviorProfile: {
      initiative: 45,
      risktolerance: 25,
      autonomy: 35,
      collaboration: 95
    },
    securityClearance: 'RESTRICTED',
    deploymentTarget: 'Support Center'
  }
];

export default function PersonaTierPanel() {
  const [personas, setPersonas] = useState<PersonaTier[]>(safeFallback(mockPersonas) || []);
  const [selectedPersona, setSelectedPersona] = useState<PersonaTier | null>(null);
  const [overrideMode, setOverrideMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [securityStatus, setSecurityStatus] = useState<string | null>(null);

  useEffect(() => {
    const loadPersonaData = async () => {
      const [personaData, healthData] = await Promise.all([
        fetchPersonas(),
        fetchHealth(),
      ]);

      if (healthData?.securityStatus) {
        setSecurityStatus(typeof healthData.securityStatus === 'string' ? healthData.securityStatus : healthData.securityStatus.status || 'unknown');
      }

      if (personaData?.personas && Array.isArray(personaData.personas) && personaData.personas.length > 0) {
        const mapped: PersonaTier[] = personaData.personas.map((p: any) => ({
          id: p.id || p.personaId || 'unknown',
          name: p.name || p.title || 'Unnamed Persona',
          tier: mapTier(p.tier),
          status: mapPersonaStatus(p.status),
          description: p.description || p.purpose || '',
          capabilities: p.capabilities || [],
          accessLevel: p.accessLevel ?? p.access_level ?? 50,
          lastAccessed: p.lastAccessed || p.last_accessed || new Date().toISOString(),
          overrideCount: p.overrideCount ?? p.override_count ?? 0,
          behaviorProfile: {
            initiative: p.behaviorProfile?.initiative ?? p.behavior?.initiative ?? 50,
            risktolerance: p.behaviorProfile?.risktolerance ?? p.behavior?.risktolerance ?? 50,
            autonomy: p.behaviorProfile?.autonomy ?? p.behavior?.autonomy ?? 50,
            collaboration: p.behaviorProfile?.collaboration ?? p.behavior?.collaboration ?? 50,
          },
          securityClearance: p.securityClearance || p.clearance || 'RESTRICTED',
          deploymentTarget: p.deploymentTarget || p.target || 'Default',
        }));
        setPersonas(mapped);
      }
    };

    loadPersonaData();
    const interval = setInterval(loadPersonaData, 15000);
    return () => clearInterval(interval);
  }, []);

  const mapTier = (tier: string | undefined): PersonaTier['tier'] => {
    if (!tier) return 'Delta';
    const t = tier.charAt(0).toUpperCase() + tier.slice(1).toLowerCase();
    if (['Alpha', 'Beta', 'Gamma', 'Delta', 'Omega'].includes(t)) return t as PersonaTier['tier'];
    return 'Delta';
  };

  const mapPersonaStatus = (status: string | undefined): PersonaTier['status'] => {
    if (!status) return 'active';
    const s = status.toLowerCase();
    if (s === 'active' || s === 'ready') return 'active';
    if (s === 'suspended' || s === 'disabled') return 'suspended';
    if (s === 'override') return 'override';
    if (s === 'locked') return 'locked';
    return 'active';
  };

  const getTierColor = (tier: string) => {
    return getSecurityLevelColor(tier);
  };

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'Alpha':
        return <Crown className="w-4 h-4" />;
      case 'Beta':
        return <Star className="w-4 h-4" />;
      case 'Gamma':
        return <Shield className="w-4 h-4" />;
      case 'Delta':
        return <User className="w-4 h-4" />;
      case 'Omega':
        return <Users className="w-4 h-4" />;
      default:
        return <User className="w-4 h-4" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-ag-active" />;
      case 'suspended':
        return <EyeOff className="w-4 h-4 text-ag-warning" />;
      case 'override':
        return <AlertTriangle className="w-4 h-4 text-orange-400" />;
      case 'locked':
        return <Lock className="w-4 h-4 text-ag-critical" />;
      default:
        return <User className="w-4 h-4 text-[#A8A29E]" />;
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-900 text-ag-active border-green-600';
      case 'suspended':
        return 'bg-yellow-900 text-ag-warning border-yellow-600';
      case 'override':
        return 'bg-orange-900 text-orange-300 border-orange-600';
      case 'locked':
        return 'bg-red-900 text-ag-critical border-red-600';
      default:
        return 'bg-gray-900 text-[#A8A29E] border-[#4A443D]';
    }
  };

  const handlePersonaAction = (personaId: string, action: string) => {
    setPersonas(prev => prev.map(persona => {
      if (persona.id === personaId) {
        let newStatus = persona.status;
        let newOverrideCount = persona.overrideCount;

        switch (action) {
          case 'activate':
            newStatus = 'active';
            break;
          case 'suspend':
            newStatus = 'suspended';
            break;
          case 'override':
            newStatus = 'override';
            newOverrideCount += 1;
            break;
          case 'lock':
            newStatus = 'locked';
            break;
        }

        return {
          ...persona,
          status: newStatus as any,
          overrideCount: newOverrideCount,
          lastAccessed: new Date().toISOString()
        };
      }
      return persona;
    }));
  };

  const filteredPersonas = personas.filter(persona =>
    persona.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    persona.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    persona.tier.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatBehaviorValue = (value: number) => {
    if (value >= 80) return { label: 'High', color: 'text-ag-active' };
    if (value >= 60) return { label: 'Medium', color: 'text-ag-warning' };
    return { label: 'Low', color: 'text-ag-critical' };
  };

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <Input
            placeholder="Search agents..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-64 bg-gray-900 border-[#4A443D] text-white"
          />
          <Button
            variant={overrideMode ? "default" : "outline"}
            onClick={() => setOverrideMode(!overrideMode)}
            className={overrideMode ? "bg-orange-600 hover:bg-orange-700" : "border-orange-600 text-orange-400 hover:bg-orange-900"}
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Admin Mode {overrideMode ? 'ON' : 'OFF'}
          </Button>
        </div>

        <div className="flex space-x-2">
          <Badge variant="outline" className="text-cyan-400 border-cyan-400">
            Security Access Level: {selectedPersona?.accessLevel || 'None'}
          </Badge>
          <Badge variant="outline" className="text-ag-active border-ag-active">
            {filteredPersonas.filter(p => p.status === 'active').length} Active Agents
          </Badge>
          {securityStatus && (
            <Badge variant="outline" className={`${securityStatus === 'ok' || securityStatus === 'healthy' ? 'text-ag-active border-ag-active' : 'text-ag-warning border-ag-warning'}`}>
              Security: {securityStatus.toUpperCase()}
            </Badge>
          )}
        </div>
      </div>

      {/* Personas Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredPersonas.map(persona => (
          <Card
            key={persona.id}
            className={`bg-gray-900 border-[#3A352F] hover:border-[#4A443D] transition-all cursor-pointer ${
              selectedPersona?.id === persona.id ? 'ring-2 ring-cyan-500' : ''
            }`}
            onClick={() => setSelectedPersona(persona)}
          >
            <CardHeader className="pb-3">
              <div className="flex justify-between items-start">
                <div className="flex items-center space-x-3">
                  <div
                    className="p-2 rounded-lg"
                    style={{ backgroundColor: getTierColor(persona.tier) + '20', color: getTierColor(persona.tier) }}
                  >
                    {getTierIcon(persona.tier)}
                  </div>
                  <div>
                    <CardTitle className="text-lg text-white">{persona.name}</CardTitle>
                    <div className="flex items-center space-x-2 mt-1">
                      <Badge
                        variant="outline"
                        className="text-xs border-[#4A443D]"
                        style={{ color: getTierColor(persona.tier), borderColor: getTierColor(persona.tier) }}
                      >
                        Tier {persona.tier}
                      </Badge>
                      <Badge className={`${getStatusBadgeColor(persona.status)} text-xs`}>
                        {getStatusIcon(persona.status)}
                        <span className="ml-1">{persona.status.toUpperCase()}</span>
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="text-right text-sm text-[#A8A29E]">
                  <div>Access: {persona.accessLevel}%</div>
                  <div>Actions: {persona.overrideCount}</div>
                </div>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <p className="text-[#A8A29E] text-sm">{persona.description}</p>

              {/* Capabilities */}
              <div>
                <h4 className="text-xs font-semibold text-[#A8A29E] mb-2">CAPABILITIES</h4>
                <div className="flex flex-wrap gap-1">
                  {persona.capabilities.slice(0, 3).map(capability => (
                    <Badge key={capability} variant="outline" className="text-xs text-[#A8A29E] border-[#4A443D]">
                      {capability}
                    </Badge>
                  ))}
                  {persona.capabilities.length > 3 && (
                    <Badge variant="outline" className="text-xs text-gray-500 border-[#4A443D]">
                      +{persona.capabilities.length - 3} more
                    </Badge>
                  )}
                </div>
              </div>

              {/* Behavior Profile */}
              <div>
                <h4 className="text-xs font-semibold text-[#A8A29E] mb-2">BEHAVIOR PROFILE</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex justify-between">
                    <span>Initiative:</span>
                    <span className={formatBehaviorValue(persona.behaviorProfile.initiative).color}>
                      {formatBehaviorValue(persona.behaviorProfile.initiative).label}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Risk Tolerance:</span>
                    <span className={formatBehaviorValue(persona.behaviorProfile.risktolerance).color}>
                      {formatBehaviorValue(persona.behaviorProfile.risktolerance).label}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Autonomy:</span>
                    <span className={formatBehaviorValue(persona.behaviorProfile.autonomy).color}>
                      {formatBehaviorValue(persona.behaviorProfile.autonomy).label}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Collaboration:</span>
                    <span className={formatBehaviorValue(persona.behaviorProfile.collaboration).color}>
                      {formatBehaviorValue(persona.behaviorProfile.collaboration).label}
                    </span>
                  </div>
                </div>
              </div>

              {/* Security & Deployment Info */}
              <div className="grid grid-cols-2 gap-4 text-xs text-[#A8A29E] pt-2 border-t border-[#3A352F]">
                <div>
                  <span className="font-medium">Clearance:</span> {persona.securityClearance}
                </div>
                <div>
                  <span className="font-medium">Target:</span> {persona.deploymentTarget}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-2 pt-2">
                {persona.status === 'active' && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); handlePersonaAction(persona.id, 'suspend'); }}
                      className="border-ag-warning text-ag-warning hover:bg-yellow-900"
                    >
                      <EyeOff className="w-3 h-3 mr-1" />
                      Suspend
                    </Button>
                    {overrideMode && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handlePersonaAction(persona.id, 'override'); }}
                        className="border-orange-600 text-orange-400 hover:bg-orange-900"
                      >
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Escalate
                      </Button>
                    )}
                  </>
                )}

                {(persona.status === 'suspended' || persona.status === 'override') && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); handlePersonaAction(persona.id, 'activate'); }}
                    className="border-ag-active text-ag-active hover:bg-green-900"
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    Activate
                  </Button>
                )}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => e.stopPropagation()}
                  className="border-[#4A443D] text-[#A8A29E] hover:bg-[#292524]"
                >
                  <Settings className="w-3 h-3 mr-1" />
                  Config
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Selected Persona Details Panel */}
      {selectedPersona && (
        <Card className="bg-gray-900 border-cyan-500">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Detailed View: {selectedPersona.name}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedPersona(null)}
                className="border-[#4A443D] text-[#A8A29E]"
              >
                Close
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Extended Capabilities */}
            <div>
              <h4 className="text-sm font-semibold text-[#A8A29E] mb-3">ALL CAPABILITIES</h4>
              <div className="grid grid-cols-3 gap-2">
                {selectedPersona.capabilities.map(capability => (
                  <Badge key={capability} variant="outline" className="text-xs text-[#A8A29E] border-[#4A443D] justify-center">
                    {capability}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Detailed Behavior Metrics */}
            <div>
              <h4 className="text-sm font-semibold text-[#A8A29E] mb-3">BEHAVIOR METRICS</h4>
              <div className="space-y-3">
                {Object.entries(selectedPersona.behaviorProfile).map(([key, value]) => (
                  <div key={key}>
                    <div className="flex justify-between mb-1">
                      <span className="text-sm capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                      <span className={`text-sm ${formatBehaviorValue(value).color}`}>{value}%</span>
                    </div>
                    <div className="w-full bg-[#3A352F] rounded-full h-2">
                      <div
                        className="bg-[#D4825F] h-2 rounded-full transition-all duration-500"
                        style={{ width: `${value}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Access History */}
            <div>
              <h4 className="text-sm font-semibold text-[#A8A29E] mb-3">ACCESS INFORMATION</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-[#A8A29E]">Last Accessed:</span>
                  <div className="text-white">{new Date(selectedPersona.lastAccessed).toLocaleString()}</div>
                </div>
                <div>
                  <span className="text-[#A8A29E]">Action Count:</span>
                  <div className="text-white">{selectedPersona.overrideCount} events</div>
                </div>
                <div>
                  <span className="text-[#A8A29E]">Security Clearance:</span>
                  <div className="text-white">{selectedPersona.securityClearance}</div>
                </div>
                <div>
                  <span className="text-[#A8A29E]">Deployment Target:</span>
                  <div className="text-white">{selectedPersona.deploymentTarget}</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {filteredPersonas.length === 0 && (
        <Card className="bg-gray-900 border-[#3A352F]">
          <CardContent className="p-12 text-center">
            <Users className="w-12 h-12 text-[#8F857B] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#A8A29E] mb-2">No agents found</h3>
            <p className="text-gray-500">Try adjusting your search criteria</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
